import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Catalog, CATALOG_STATUS_LABELS, CatalogStatus } from '../../models/catalog';
import { CatalogService } from '../../../services/catalog.service';

/**
 * The global material catalogs (manufacturers' price lists and ranges) that tenants with
 * FEATURE_CATALOG can import materials from. Only PUBLISHED ones are visible to tenants.
 *
 * The list is short — one per manufacturer or distributor — so it is not paged.
 */
@Component({
  selector: 'app-catalogs',
  templateUrl: './catalogs.component.html',
  styleUrls: ['./catalogs.component.scss']
})
export class CatalogsComponent implements OnInit {

  catalogs: Catalog[] = [];
  loading = true;
  refreshing = false;
  error: string | null = null;

  formOpen = false;
  /** The catalog being edited, or null while creating one. */
  editing: Catalog | null = null;

  constructor(private catalogService: CatalogService, private router: Router) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.refreshing = !this.loading;
    this.error = null;

    this.catalogService.getCatalogs().subscribe({
      next: (response) => {
        this.catalogs = response.data ?? [];
        this.loading = false;
        this.refreshing = false;
      },
      error: () => {
        this.error = 'Could not load catalogs.';
        this.loading = false;
        this.refreshing = false;
      }
    });
  }

  openCreate(): void {
    this.editing = null;
    this.formOpen = true;
  }

  openEdit(catalog: Catalog, event?: Event): void {
    // The button sits in a clickable row; without this it would also open the detail page.
    event?.stopPropagation();
    this.editing = catalog;
    this.formOpen = true;
  }

  onSaved(catalog: Catalog): void {
    const created = !this.editing;
    this.formOpen = false;
    this.editing = null;
    if (created) {
      // A new catalog is empty; the next thing to do with it is upload its CSV, which is there.
      this.router.navigate(['/catalogs', catalog.id]);
      return;
    }
    this.catalogs = this.catalogs.map(c => c.id === catalog.id ? catalog : c);
  }

  open(catalog: Catalog): void {
    this.router.navigate(['/catalogs', catalog.id]);
  }

  statusLabel(status: CatalogStatus): string {
    return CATALOG_STATUS_LABELS[status] ?? status;
  }

  statusClass(status: CatalogStatus): string {
    return `catalog-status status-${(status || 'draft').toLowerCase()}`;
  }

  trackById(_index: number, catalog: Catalog): number {
    return catalog.id;
  }
}
